import React, { useState } from 'react';
import { Star } from 'lucide-react';

// Read-only fractional star display OR interactive 1-5 picker.
export const StarRating = ({
  value = 0,
  count = null,
  size = 'md',
  interactive = false,
  onChange,
  showNumber = false,
  className = '',
  emptyLabel = 'No ratings yet',
  testId,
}) => {
  const [hover, setHover] = useState(0);
  const px = size === 'sm' ? 'w-3.5 h-3.5' : size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';

  if (interactive) {
    const active = hover || value;
    return (
      <div className={`flex items-center gap-1 ${className}`} data-testid={testId} role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            onClick={() => onChange && onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            data-testid={testId ? `${testId}-star-${n}` : undefined}
          >
            <Star className={`${px} ${n <= active ? 'fill-amber-400 text-amber-400' : 'text-shrimp-border'}`} />
          </button>
        ))}
      </div>
    );
  }

  // Read-only: show "No ratings yet" when there is no rating
  if (!value || (count !== null && count === 0)) {
    return <span className={`text-xs text-shrimp-text/70 ${className}`} data-testid={testId}>{emptyLabel}</span>;
  }

  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testId} aria-label={`${value} out of 5`}>
      <span className="relative inline-block leading-none">
        {/* empty base */}
        <span className="flex">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`${px} text-shrimp-border`} />
          ))}
        </span>
        {/* gold overlay clipped to pct */}
        <span className="absolute inset-0 flex overflow-hidden" style={{ width: `${pct}%` }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`${px} fill-amber-400 text-amber-400 shrink-0`} />
          ))}
        </span>
      </span>
      {showNumber && <span className="text-sm font-semibold text-shrimp-dark">{value.toFixed(1)}</span>}
      {count !== null && count > 0 && (
        <span className="text-xs text-shrimp-text/70">({count})</span>
      )}
    </div>
  );
};

export default StarRating;
